import React, { useContext, useEffect, useRef, useState } from "react";
import "../../../modals/Modal.css";
import "../../../components/map/Map.css";
import {
  StepNavPanel,
  type StepNavPanelHandle,
} from "../../../modals/StepNavPanel";
import { PickupLocationContext } from "../../../store/PickupLocationContext";
import { SeasonContext } from "../../../store/SeasonContext";
import {
  PickupLocationForm,
  PICKUP_FORM_ID,
  type PickupLocationSubmitPayload,
} from "./PickupLocationForm";

export type PickupLocationModalBodyProps = {
  onClose: () => void;
  idToEdit: string;
};
//TODO should not depend on season but on application period
export const PickupLocationModalBody = ({
  onClose,
  idToEdit,
}: PickupLocationModalBodyProps) => {
  const {
    pickupLocations,
    updatePickupLocation,
    createPickupLocation,
    deletePickupLocation,
  } = useContext(PickupLocationContext);
  const { areWeInSeason } = useContext(SeasonContext);
  const carouselRef = useRef<StepNavPanelHandle>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmDraft, setConfirmDraft] =
    useState<PickupLocationSubmitPayload | null>(null);
  const [resultMessage, setResultMessage] = useState<string | null>(null);
  const activePickup = pickupLocations.find((p) => p._id === idToEdit);
  const isEditMode = Boolean(idToEdit);

  const FORM_STEP = 0;
  const UPDATE_CONFIRM_STEP = 1;
  const DELETE_CONFIRM_STEP = 2;
  const RESULT_STEP = isEditMode ? 3 : 1;

  useEffect(() => {
    setConfirmDraft(null);
    setResultMessage(null);
  }, [idToEdit]);

  async function handleFormSubmit(payload: PickupLocationSubmitPayload) {
    if (isSubmitting) return;

    if (activePickup) {
      setConfirmDraft(payload);
      carouselRef.current?.goToStep(UPDATE_CONFIRM_STEP);
      return;
    }

    setIsSubmitting(true);
    try {
      await createPickupLocation(
        payload.name,
        payload.address,
        Number(payload.latitude),
        Number(payload.longitude),
        payload.days,
      );
      setResultMessage("The pickup location has been created successfully.");
      carouselRef.current?.goToStep(RESULT_STEP);
    } catch {
      setResultMessage("Failed to create pickup location.");
      carouselRef.current?.goToStep(RESULT_STEP);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleConfirmUpdate() {
    if (isSubmitting || !activePickup || !confirmDraft) return;
    setIsSubmitting(true);
    try {
      await updatePickupLocation(
        activePickup._id,
        confirmDraft.name,
        confirmDraft.address,
        Number(confirmDraft.latitude),
        Number(confirmDraft.longitude),
        confirmDraft.days,
      );
      setResultMessage("The pickup location has been updated successfully.");
      carouselRef.current?.goToStep(RESULT_STEP);
    } catch {
      setResultMessage("Failed to update pickup location.");
      carouselRef.current?.goToStep(RESULT_STEP);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleConfirmDelete() {
    if (!activePickup || isSubmitting) return;
    const targetId = activePickup._id;
    const targetName = activePickup.name;
    setIsSubmitting(true);
    try {
      await deletePickupLocation(targetId);
      setResultMessage(`"${targetName}" was deleted successfully.`);
      carouselRef.current?.goToStep(RESULT_STEP);
    } catch {
      setResultMessage("Failed to delete pickup location.");
      carouselRef.current?.goToStep(RESULT_STEP);
    } finally {
      setIsSubmitting(false);
    }
  }

  const confirmSummary =
    activePickup && confirmDraft ? (
      <>
        <p>Are you sure you want to change this pickup location to:</p>
        <br />
        <p>
          <strong>Name:</strong> {confirmDraft.name}
        </p>
        <p>
          <strong>Address:</strong> {confirmDraft.address}
        </p>
        <p>
          <strong>Pickup Times:</strong>
          {confirmDraft.days.map(
            (d) => ` ${d.day}: ${d.startTime}-${d.endTime}`,
          )}
        </p>
      </>
    ) : null;

  const formFooter = (
    <>
      <div className="buttons-footer">
        <button type="button" className="btn equal-btns" onClick={onClose}>
          Cancel
        </button>

        <button
          type="submit"
          form={PICKUP_FORM_ID}
          className="btn equal-btns"
          disabled={isSubmitting}
        >
          {isSubmitting
            ? activePickup
              ? "Saving..."
              : "Creating..."
            : activePickup
              ? "Save"
              : "Create"}
        </button>
      </div>
      {isEditMode && !areWeInSeason && (
        <button
          type="button"
          className={
            activePickup?.undeletable
              ? "btn delete-btn disabled"
              : "btn delete-btn"
          }
          disabled={activePickup?.undeletable}
          onClick={() => carouselRef.current?.goToStep(DELETE_CONFIRM_STEP)}
        >
          Delete
        </button>
      )}
    </>
  );

  const confirmFooter = (
    <div className="buttons-footer">
      <button
        type="button"
        className="btn equal-btns"
        disabled={isSubmitting}
        onClick={() => carouselRef.current?.goToStep(FORM_STEP)}
      >
        Back
      </button>
      <button
        type="button"
        className="btn equal-btns"
        disabled={isSubmitting}
        onClick={() => void handleConfirmUpdate()}
      >
        {isSubmitting ? "Saving..." : "Confirm"}
      </button>
    </div>
  );

  const deleteConfirmContent = (
    <div className="edit-inputs">
      <p className="modal-exit-confirm-msg">
        Are you sure you want to delete{" "}
        <span className="bold capitalized">{activePickup?.name}</span>?
      </p>
    </div>
  );

  const deleteConfirmFooter = (
    <div className="buttons-footer">
      <button
        type="button"
        className="btn equal-btns"
        disabled={isSubmitting}
        onClick={() => carouselRef.current?.goToStep(FORM_STEP)}
      >
        No
      </button>
      <button
        type="button"
        className="btn equal-btns"
        disabled={isSubmitting}
        onClick={() => void handleConfirmDelete()}
      >
        {isSubmitting ? "Deleting..." : "Yes"}
      </button>
    </div>
  );

  const resultContent = (
    <div className="modal-result-step">
      <p className="modal-result-title">Success!</p>
      <p>{resultMessage ?? "Action completed."}</p>
    </div>
  );

  const resultFooter = (
    <div className="buttons-footer">
      <button type="button" className="btn equal-btns" onClick={onClose}>
        Close
      </button>
    </div>
  );

  const formPanel = (
    <PickupLocationForm
      activePickup={activePickup}
      onSubmit={handleFormSubmit}
      submitBusy={isSubmitting}
    />
  );

  const confirmContent = (
    <div className="pickup-modal-wrapper">
      <div className="pickup-confirm-step">{confirmSummary}</div>
    </div>
  );

  const steps = isEditMode
    ? [
        { content: formPanel, footer: formFooter },
        { content: confirmContent, footer: confirmFooter },
        { content: deleteConfirmContent, footer: deleteConfirmFooter },
        { content: resultContent, footer: resultFooter },
      ]
    : [
        { content: formPanel, footer: formFooter },
        { content: resultContent, footer: resultFooter },
      ];

  return (
    <StepNavPanel
      key={idToEdit}
      ref={carouselRef}
      steps={steps}
      className="pickup-modal-wrapper"
    />
  );
};
